import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import {
  DeleteButton,
  EditButton,
  GreenButton,
  PrimaryButton,
  PurpleButton,
} from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';

import { useTranslation } from 'react-i18next';
import { RootState, store } from '@/state';
import {
  deleteSubProjectMaterial,
  saveSubProjectMaterialsAsync,
} from '@/state/slices/subProjectMethodSlice';
import { ThunkDispatch, AnyAction } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PlusCircleIcon } from '@heroicons/react/solid';
import SubProjectMaterialModal, { ModalProps } from './SubProjectMaterialModal';
import { formatCurrency } from '@/utils/helpers';
import { Currency } from '@/enums/Currency';
import {
  SubProjectMaterial,
  SubProjectMaterialsPayload,
} from '@/services/SubProjectMethodsService';

function SubProjectMaterials() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMaterialModel, setIsMaterialModel] = useState(false);
  const [initMaterialData, setInitMaterialDate] =
    useState<SubProjectMaterial>();
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();

  const { data, status } = useSelector(
    (state: RootState) => state.subProjectMethod!
  );
  let projectCurreny = data?.currency ?? Currency.EUR;

  let subProjectId = data?.id;
  let subProjectMaterials = useMemo(
    () => data?.subProjectMaterials ?? [],
    [data, status]
  );

  const ActionButtonsCell = ({ row, value }: any) => (
    <div>
      <DeleteButton
        onClick={() => {
          setSelectedRow(value);
          setIsModalVisible(true);
        }}
      >
        {t('SubProjectMaterials.deleteButton')}
      </DeleteButton>
      <EditButton
        type="button"
        onClick={() => {
          setInitMaterialDate((pr) => row.original);
          setIsMaterialModel(true);
        }}
      >
        {t('SubProjectMaterials.editButton')}
      </EditButton>
    </div>
  );

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteSubProjectMaterial.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('SubProjectMaterials.actionsColumn'),
        accessor: 'id',
        Cell: ({ row, cell: { value } }: any) => (
          <ActionButtonsCell value={value} row={row} />
        ),
        Footer: (infos) => {
          return <>Total</>;
        },
      },
      {
        Header: t('SubProjectMaterials.equipmentIdColumn'),
        accessor: 'name',
      },
      {
        Header: t('SubProjectMaterials.descriptionColumn'),
        accessor: 'description',
        Footer: (infos) => {
          return <></>;
        },
      },

      {
        Header: t('SubProjectMaterials.quantityColumn'),
        accessor: 'quantity',
        Footer: (infos) => {
          console.log('🚀 ~ SubProjectMaterials ~ infos:', infos);
          var total = useMemo(
            () => infos.rows.reduce((sum, row) => row.values.quantity + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectMaterials.capacityColumn'),
        accessor: 'capacity',
        Footer: (infos) => {
          var total = useMemo(
            () => infos.rows.reduce((sum, row) => row.values.capacity + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectMaterials.unitPriceColumn'),
        accessor: 'unitPrice',
        Cell: ({ row, cell: { value } }: any) => {
          var currency = row.original['equipmentCurrency'];
          return <span> {formatCurrency(value, currency)} </span>;
        },
        Footer: (infos) => {
          return <></>;
        },
      },
      {
        Header: t('SubProjectMaterials.totalPriceColumn'),
        accessor: 'totalPrice',
        Cell: ({ row, cell: { value } }: any) => {
          var currency = row.original['equipmentCurrency'];
          return <span> {formatCurrency(value, currency)} </span>;
        },
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) =>
                  parseFloat(row.values.totalPrice) + parseFloat(sum),
                0
              ),

            [infos.rows]
          );
          return <>{total.toFixed(4)}</>;
        },
      },

      {
        Header: t('SubProjectMaterials.exchangeRateColumn'),
        accessor: 'exchangeRate',
        Footer: (infos) => {
          return <></>;
        },
      },
      // {
      //   Header: t('SubProjectMaterials.exchangeRateColumn'),
      //   accessor: 'exchangeRate',
      // },
      {
        Header: t('SubProjectMaterials.TotalWithCurrencyColumn'),
        accessor: 'totalToCurrency',
        Cell: ({ cell: { value } }: any) => (
          <span> {formatCurrency(value, projectCurreny)} </span>
        ),
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) =>
                  parseFloat(row.values.totalToCurrency) + parseFloat(sum),
                0
              ),

            [infos.rows]
          );
          return <>{formatCurrency(total.toFixed(4), projectCurreny)}</>;
        },
      },

      {
        Header: t('SubProjectMaterials.commentsColumn'),
        accessor: 'comments',
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const handleDeleteSubProjectMaterial = useMutation(
    async (id: string) => {
      await dispatch(deleteSubProjectMaterial(id));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'subProjectMaterials',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((subProjectMaterials?.length ?? 0) / pageSize);
  }, [pageSize, subProjectMaterials]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  //? save Materials
  const handleSaveMaterials = async () => {
    var payload: SubProjectMaterialsPayload = {
      subProjectMaterials: subProjectMaterials.map((sm) => ({
        ...sm,
        id: sm.id?.includes('new') ? null : sm.id,
      })),
      subProjectMethodId: data?.id!,
    };
    const result = await dispatch(saveSubProjectMaterialsAsync(payload));
  };

  return (
    <div>
      {/* <PlusCircleIcon
        className="h-7 w-7 text-blue-500"
        onClick={(e) => {
          setInitMaterialDate(undefined);
          setIsMaterialModel(true);
        }}
      /> */}
      <PrimaryButton
        type="button"
        onClick={() => {
          setInitMaterialDate(undefined);
          setIsMaterialModel(true);
        }}
        className="mt-5"
      >
        {t('expenses.add')}
      </PrimaryButton>

      {subProjectId && (
        <PurpleButton
          type="button"
          onClick={handleSaveMaterials}
          className="mt-5 py-2.5"
        >
          {t('stepper.save')}
        </PurpleButton>
      )}
      {status == 'loading' ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={subProjectMaterials?.length}
            data={subProjectMaterials ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
            exportExcel={false}
            hideToolbar={true}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          {isMaterialModel && (
            <SubProjectMaterialModal
              closeModal={() => setIsMaterialModel(false)}
              initialData={initMaterialData as ModalProps}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
}

export default SubProjectMaterials;
